"use client";

import { useState } from "react";
import Image from "next/image";
import { useTrackEvent } from "@/hooks/useTrackEvent";
import { singingTipsVideos } from "@/lib/singingTipsVideos";
import styles from "./SingingTipsVideos.module.css";

export function SingingTipsVideos() {
  // Keep only one player active so a second choice stops the previous clip.
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  const trackEvent = useTrackEvent();

  return (
    <section id="singing-videos" className={styles.section} aria-labelledby="singing-videos-title">
      <div className={styles.heading}>
        <div>
          <p className={styles.eyebrow}>Watch · listen · try</p>
          <h2 id="singing-videos-title">Small lessons. Hear the difference.</h2>
        </div>
        <p>Standout singing tips from Liuba&apos;s social videos, selected for useful demonstrations and listening exercises.</p>
      </div>
      <div className={styles.grid}>
        {singingTipsVideos.map((video) => (
          <article key={video.id} className={styles.card}>
            <div className={styles.preview}>
              {activeVideo === video.id ? (
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${video.id}?rel=0&autoplay=1`}
                  title={video.title}
                  allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              ) : (
                <button
                  type="button"
                  aria-label={`Play: ${video.title}`}
                  onClick={() => {
                    setActiveVideo(video.id);
                    trackEvent("singing_tip_video_play", { video_id: video.id, video_title: video.title, placement: "blog" });
                  }}
                >
                  <Image
                    src={`/images/singing-tips/${video.id}.webp`}
                    alt=""
                    fill
                    sizes="(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 90vw"
                    className={styles.poster}
                  />
                  <span className={styles.play} aria-hidden="true">
                    <svg viewBox="0 0 24 24"><path d="M8 5.5v13l10-6.5-10-6.5Z" /></svg>
                  </span>
                  <span className={styles.videoLabel}>Watch the short</span>
                </button>
              )}
            </div>
            <div className={styles.copy}>
              <p className={styles.eyebrow}>{video.topic}</p>
              <h3>{video.title}</h3>
              <p className={styles.summary}>{video.summary}</p>
              <a
                href={`https://www.youtube.com/shorts/${video.id}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("singing_tip_youtube_click", { video_id: video.id, placement: "blog" })}
              >
                Watch on YouTube <span aria-hidden="true">↗</span><span className="sr-only">: {video.title}</span>
              </a>
            </div>
          </article>
        ))}
      </div>
      <div className={styles.footnote}>
        <p>Selected using our August 2026 social video review and Liuba&apos;s published Shorts. Each voice is different: use the clips to notice patterns, then bring your questions to a lesson.</p>
        <p>Videos load from YouTube only when you press play. You can also open each clip on YouTube.</p>
      </div>
    </section>
  );
}
